<template>
    <div class="tl-spatial-map">
      <div v-if="!farmGeo" class="tl-spatial-map__empty d-flex align-center justify-center">
        <v-icon color="grey lighten-1" class="mr-2">mdi-map-marker-off</v-icon>
        <span class="text-caption grey--text">No location data available</span>
      </div>
      <template v-else>
        <div ref="mapEl" class="tl-spatial-map__canvas" :style="{ height: height + 'px' }"></div>
        <div v-if="loading" class="tl-spatial-map__loading d-flex align-center justify-center">
          <v-progress-circular indeterminate size="28" width="3" color="primary" />
        </div>
        <div class="tl-spatial-map__legend">
          <div
            v-for="item in legendItems"
            :key="item.key"
            class="tl-spatial-map__legend-item"
            :class="{ 'tl-spatial-map__legend-item--inactive': !item.active }"
          >
            <span class="tl-spatial-map__legend-swatch" :style="{ background: item.color }"></span>
            <span class="text-caption">{{ item.label }}</span>
          </div>
        </div>
        <div v-if="viewMode === 'region' && regencyName" class="tl-spatial-map__regency-badge">
          <v-icon x-small class="mr-1" color="white">mdi-map-marker-radius</v-icon>
          {{ regencyName }}
        </div>
        <div v-if="viewMode === 'farm' && farmGeo.farmName" class="tl-spatial-map__farm-badge">
          {{ farmGeo.farmName }}
        </div>
      </template>
    </div>
  </template>
  
  <script>
  import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton'
  
  export function resolveFarmGeo(farm, farmType) {
    if (!farm) return null
  
    const lat = parseFloat(farm.lat)
    const lng = parseFloat(farm.log)
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  
    let paths = null
    let radiusMeters = null
  
    if (farmType && farmType.toLowerCase() === 'polygon' && farm.FarmCoordinates && farm.FarmCoordinates.length) {
      paths = farm.FarmCoordinates.map(c => ({
        lat: parseFloat(c.lat),
        lng: parseFloat(c.log),
      })).filter(p => !Number.isNaN(p.lat) && !Number.isNaN(p.lng))
    }
  
    if (!paths || !paths.length) {
      const geofence = farm.GeoFences && farm.GeoFences[0]
      if (geofence && geofence.geofenceRadius) {
        radiusMeters = parseFloat(geofence.geofenceRadius)
      } else {
        const d = 0.00135
        paths = [
          { lat: lat - d, lng: lng - d },
          { lat: lat - d, lng: lng + d },
          { lat: lat + d, lng: lng + d },
          { lat: lat + d, lng: lng - d },
        ]
      }
    }
  
    return {
      lat,
      lng,
      paths,
      radiusMeters,
      farmName: farm.farmName || '',
    }
  }
  
  export default {
    name: 'TrafficLightSpatialMap',
    props: {
      farm: { type: Object, default: null },
      farmType: { type: String, default: 'point' },
      phase1: {
        type: Object,
        default: () => ({ deforestation: false, forestOverlap: false, bufferZone: false }),
      },
      viewMode: {
        type: String,
        default: 'farm',
        validator: v => ['farm', 'region'].includes(v),
      },
      regencyName: { type: String, default: '' },
      riskColor: { type: String, default: '#489E16' },
      height: { type: Number, default: 200 },
      active: { type: Boolean, default: true },
    },
    data() {
      return {
        loading: true,
        google: null,
        map: null,
        overlays: [],
      }
    },
    computed: {
      farmGeo() {
        return resolveFarmGeo(this.farm, this.farmType)
      },
      legendItems() {
        if (this.viewMode === 'region') {
          return [
            { key: 'farm', label: 'Farm location', color: '#1976D2', active: true },
            { key: 'region', label: 'Regency area', color: this.riskColor, active: true },
          ]
        }
        return [
          { key: 'farm', label: 'Farm boundary', color: '#1F5D51', active: true },
          { key: 'deforestation', label: 'Deforestation (post-2020)', color: '#DA3D3D', active: this.phase1.deforestation },
          { key: 'forest', label: 'Protected forest', color: '#2E7D32', active: this.phase1.forestOverlap },
          { key: 'buffer', label: '100m forest buffer', color: '#FFA826', active: this.phase1.bufferZone },
        ]
      },
    },
    watch: {
      farmGeo: {
        handler() {
          this.$nextTick(() => this.refreshMap())
        },
        deep: true,
      },
      phase1: {
        handler() {
          this.$nextTick(() => this.refreshMap())
        },
        deep: true,
      },
      viewMode() {
        this.$nextTick(() => this.refreshMap())
      },
      regencyName() {
        this.$nextTick(() => this.refreshMap())
      },
      riskColor() {
        this.$nextTick(() => this.refreshMap())
      },
      active(val) {
        if (val) {
          this.scheduleMapRefresh()
        }
      },
    },
    mounted() {
      if (this.active) {
        this.scheduleMapRefresh()
      }
    },
    beforeDestroy() {
      this.clearOverlays()
      this.map = null
    },
    methods: {
      scheduleMapRefresh() {
        this.$nextTick(() => {
          setTimeout(() => this.initMap(), 400)
          setTimeout(() => this.resizeAndRedraw(), 900)
        })
      },
      resizeAndRedraw() {
        if (!this.google || !this.map) return
        this.google.maps.event.trigger(this.map, 'resize')
        this.map.setCenter({ lat: this.farmGeo.lat, lng: this.farmGeo.lng })
        this.fitBounds()
      },
      async initMap() {
        if (!this.farmGeo || !this.$refs.mapEl) {
          this.loading = false
          return
        }
        if (this.map) {
          this.resizeAndRedraw()
          return
        }
        try {
          this.loading = true
          const loader = getGoogleMapsLoader()
          this.google = await loader.load()
          const { lat, lng } = this.farmGeo
          this.map = new this.google.maps.Map(this.$refs.mapEl, {
            mapTypeId: this.google.maps.MapTypeId.SATELLITE,
            center: { lat, lng },
            zoom: 17,
            minZoom: 14,
            maxZoom: 20,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'cooperative',
          })
          this.drawLayers()
          this.fitBounds()
          this.$nextTick(() => this.resizeAndRedraw())
        } catch (e) {
          console.error('TrafficLightSpatialMap init error', e)
        } finally {
          this.loading = false
        }
      },
      async refreshMap() {
        if (!this.farmGeo) return
        if (!this.map) {
          await this.initMap()
          return
        }
        this.clearOverlays()
        this.drawLayers()
        this.fitBounds()
        this.resizeAndRedraw()
      },
      clearOverlays() {
        this.overlays.forEach(o => o.setMap(null))
        this.overlays = []
      },
      addOverlay(overlay) {
        overlay.setMap(this.map)
        this.overlays.push(overlay)
      },
      drawLayers() {
        if (!this.map || !this.google || !this.farmGeo) return
  
        if (this.viewMode === 'region') {
          this.drawRegionalView()
        } else {
          this.drawFarmView()
        }
      },
      drawFarmView() {
        const { lat, lng, paths, radiusMeters } = this.farmGeo
        const hasIssue = this.phase1.deforestation || this.phase1.forestOverlap || this.phase1.bufferZone
        const farmStroke = hasIssue ? '#DA3D3D' : '#1F5D51'
        const farmFill = hasIssue ? '#DA3D3D' : '#489E16'
  
        if (paths && paths.length) {
          this.addOverlay(new this.google.maps.Polygon({
            paths,
            strokeColor: farmStroke,
            strokeOpacity: 0.95,
            strokeWeight: 3,
            fillColor: farmFill,
            fillOpacity: 0.22,
            zIndex: 3,
          }))
        } else if (radiusMeters) {
          this.addOverlay(new this.google.maps.Circle({
            center: { lat, lng },
            radius: radiusMeters,
            strokeColor: farmStroke,
            strokeOpacity: 0.95,
            strokeWeight: 3,
            fillColor: farmFill,
            fillOpacity: 0.22,
            zIndex: 3,
          }))
        }
  
        if (this.phase1.deforestation) {
          const patch = this.offsetPolygon(paths || this.circleToPolygon(lat, lng, radiusMeters || 120), 0.00045, 0.00035, 0.55)
          this.addOverlay(new this.google.maps.Polygon({
            paths: patch,
            strokeColor: '#B71C1C',
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: '#DA3D3D',
            fillOpacity: 0.55,
            zIndex: 4,
          }))
        }
  
        if (this.phase1.forestOverlap) {
          const forest = this.offsetPolygon(paths || this.circleToPolygon(lat, lng, radiusMeters || 120), -0.0005, -0.0003, 0.75)
          this.addOverlay(new this.google.maps.Polygon({
            paths: forest,
            strokeColor: '#1B5E20',
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: '#2E7D32',
            fillOpacity: 0.45,
            zIndex: 2,
          }))
        }
  
        if (this.phase1.bufferZone) {
          const baseRadius = radiusMeters || 130
          this.addOverlay(new this.google.maps.Circle({
            center: { lat: lat - 0.00055, lng: lng + 0.0004 },
            radius: baseRadius + 100,
            strokeColor: '#FFA826',
            strokeOpacity: 0.95,
            strokeWeight: 2,
            fillColor: '#FFA826',
            fillOpacity: 0.12,
            zIndex: 1,
          }))
          this.addOverlay(new this.google.maps.Circle({
            center: { lat: lat - 0.00055, lng: lng + 0.0004 },
            radius: baseRadius,
            strokeColor: '#2E7D32',
            strokeOpacity: 0.7,
            strokeWeight: 2,
            fillColor: '#2E7D32',
            fillOpacity: 0.2,
            zIndex: 1,
          }))
        }
      },
      drawRegionalView() {
        const { lat, lng } = this.farmGeo
  
        this.addOverlay(new this.google.maps.Circle({
          center: { lat, lng },
          radius: 28000,
          strokeColor: this.riskColor,
          strokeOpacity: 0.75,
          strokeWeight: 2,
          fillColor: this.riskColor,
          fillOpacity: 0.1,
          zIndex: 1,
        }))
  
        this.addOverlay(new this.google.maps.Marker({
          position: { lat, lng },
          icon: {
            path: this.google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: '#1976D2',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
          },
          zIndex: 5,
        }))
      },
      circleToPolygon(lat, lng, radiusMeters) {
        const points = 24
        const result = []
        for (let i = 0; i < points; i++) {
          const angle = (i / points) * 2 * Math.PI
          const dLat = (radiusMeters / 111320) * Math.cos(angle)
          const dLng = (radiusMeters / (111320 * Math.cos(lat * Math.PI / 180))) * Math.sin(angle)
          result.push({ lat: lat + dLat, lng: lng + dLng })
        }
        return result
      },
      offsetPolygon(paths, dLat, dLng, scale) {
        if (!paths || !paths.length) return []
        const cx = paths.reduce((s, p) => s + p.lat, 0) / paths.length
        const cy = paths.reduce((s, p) => s + p.lng, 0) / paths.length
        return paths.map(p => ({
          lat: cx + (p.lat - cx) * scale + dLat,
          lng: cy + (p.lng - cy) * scale + dLng,
        }))
      },
      fitBounds() {
        if (!this.map || !this.google || !this.farmGeo) return
  
        if (this.viewMode === 'region') {
          const bounds = new this.google.maps.LatLngBounds()
          const d = 0.35
          bounds.extend({ lat: this.farmGeo.lat - d, lng: this.farmGeo.lng - d })
          bounds.extend({ lat: this.farmGeo.lat + d, lng: this.farmGeo.lng + d })
          this.map.fitBounds(bounds, 24)
          return
        }
  
        const { lat, lng, paths, radiusMeters } = this.farmGeo
        const bounds = new this.google.maps.LatLngBounds()
        bounds.extend({ lat, lng })
  
        if (paths && paths.length) {
          paths.forEach(p => bounds.extend(p))
        } else if (radiusMeters) {
          const d = radiusMeters / 111320
          bounds.extend({ lat: lat - d, lng: lng - d })
          bounds.extend({ lat: lat + d, lng: lng + d })
        } else {
          const d = 0.002
          bounds.extend({ lat: lat - d, lng: lng - d })
          bounds.extend({ lat: lat + d, lng: lng + d })
        }
  
        this.map.fitBounds(bounds, 40)
  
        this.google.maps.event.addListenerOnce(this.map, 'idle', () => {
          const zoom = this.map.getZoom()
          if (!zoom || zoom < 15) this.map.setZoom(16)
          if (zoom > 19) this.map.setZoom(19)
        })
      },
    },
  }
  </script>
  
  <style scoped>
  .tl-spatial-map {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #e0e0e0;
    background: #eceff1;
  }
  
  .tl-spatial-map__canvas {
    width: 100%;
    min-height: 200px;
    display: block;
    background: #e8e8e8;
  }
  
  .tl-spatial-map__empty {
    height: 160px;
    background: #f5f5f5;
  }
  
  .tl-spatial-map__loading {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.7);
    z-index: 2;
  }
  
  .tl-spatial-map__legend {
    position: absolute;
    left: 8px;
    bottom: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
    max-width: calc(100% - 16px);
    padding: 6px 8px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
    z-index: 3;
  }
  
  .tl-spatial-map__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  
  .tl-spatial-map__legend-item--inactive {
    opacity: 0.35;
  }
  
  .tl-spatial-map__legend-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }
  
  .tl-spatial-map__farm-badge,
  .tl-spatial-map__regency-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 4px 10px;
    border-radius: 14px;
    font-size: 11px;
    font-weight: 700;
    z-index: 3;
    max-width: calc(100% - 16px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .tl-spatial-map__farm-badge {
    background: rgba(255, 255, 255, 0.92);
    color: #333;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  }
  
  .tl-spatial-map__regency-badge {
    background: rgba(25, 118, 210, 0.9);
    color: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }
  </style>
  